import React from "react";
import { Form, Button, Card, Badge } from "react-bootstrap";
import moment from "moment";
import "./ProjectBoardTab.css";

const ProjectBoardTab = ({
  boardGroupBy,
  setBoardGroupBy,
  getBoardColumns,
  getPriorityBadge,
  getStatusBadge,
  handleEditTask,
  getTaskStatusName,
  filteredStageTasks,
  teamMemberDetails,
  clientContactPersons,
  partnerContactPersons,
  contactNamesCache,
  taskAssignmentsByTask,
}) => {
  // Add debug logging
  React.useEffect(() => {
    console.log("ProjectBoardTab - boardGroupBy:", boardGroupBy);
    console.log("ProjectBoardTab - filteredStageTasks:", filteredStageTasks);
    console.log(
      "ProjectBoardTab - taskAssignmentsByTask:",
      taskAssignmentsByTask,
    );

    // Log all tasks and their assignments
    if (filteredStageTasks) {
      Object.values(filteredStageTasks).forEach((tasks) => {
        if (tasks && Array.isArray(tasks)) {
          tasks.forEach((task) => {
            const assignments = taskAssignmentsByTask?.[task.taskId];
            console.log(
              `Task ${task.taskId} (${task.taskName}) has ${assignments?.length || 0} assignments:`,
              assignments,
            );
          });
        }
      });
    }
  }, [boardGroupBy, filteredStageTasks, taskAssignmentsByTask]);

  // Helper function to get assignee name (similar to ProjectTasksTab)
  const resolveAssigneeName = (userId) => {
    if (!userId) return "Unassigned";

    // Check contact names cache first (for GUIDs)
    const contactName = contactNamesCache?.[userId?.toLowerCase()];
    if (contactName) return contactName;

    // Check team members
    const teamMember = teamMemberDetails?.find(
      (user) => String(user.userId) === String(userId),
    );
    if (teamMember) return teamMember.username;

    // Check client contacts
    const clientContact = clientContactPersons?.find(
      (c) => c.cusVenId === userId || c.contactGuid === userId,
    );
    if (clientContact)
      return clientContact.contactPerName || clientContact.contactName;

    // Check partner contacts
    const partnerContact = partnerContactPersons?.find(
      (c) => c.cusVenId === userId || c.contactGuid === userId,
    );
    if (partnerContact)
      return partnerContact.contactPerName || partnerContact.contactName;

    // If it's a GUID format, return truncated version
    const isGuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        userId,
      );
    if (isGuid) return userId.substring(0, 8);

    return userId;
  };

  // Get assignments for a task
  const getTaskAssignments = (taskId) => {
    const assignments = taskAssignmentsByTask?.[taskId] || [];
    console.log(`getTaskAssignments for task ${taskId}:`, assignments);
    return assignments;
  };

  // Custom getBoardColumns function that works with GUIDs
  const getColumnsWithResolvedNames = () => {
    // Get all tasks from filteredStageTasks
    const allTasks = [];
    if (filteredStageTasks) {
      Object.values(filteredStageTasks).forEach((tasks) => {
        if (tasks && Array.isArray(tasks)) {
          allTasks.push(...tasks);
        }
      });
    }

    console.log("Total tasks for board:", allTasks.length);
    const columns = {};

    if (boardGroupBy === "status") {
      // Define status order
      const statusOrder = [
        "Not Started",
        "In Progress",
        "On Hold",
        "Completed",
        "Cancelled",
        "No Status",
      ];

      statusOrder.forEach((status) => {
        columns[status] = [];
      });

      allTasks.forEach((task) => {
        const statusName = getTaskStatusName?.(task.taskStatus) || "No Status";
        if (columns[statusName]) {
          columns[statusName].push(task);
        } else {
          // For any other status not in predefined list
          if (!columns[statusName]) {
            columns[statusName] = [];
          }
          columns[statusName].push(task);
        }
      });
    } else if (boardGroupBy === "priority") {
      const priorityOrder = [
        "Critical",
        "High",
        "Medium",
        "Low",
        "No Priority",
      ];

      priorityOrder.forEach((priority) => {
        columns[priority] = [];
      });

      allTasks.forEach((task) => {
        const priority = task.taskPriority || "No Priority";
        if (columns[priority]) {
          columns[priority].push(task);
        } else {
          if (!columns[priority]) {
            columns[priority] = [];
          }
          columns[priority].push(task);
        }
      });
    } else if (boardGroupBy === "assignee") {
      // Build assignee columns
      const assigneeMap = new Map();

      allTasks.forEach((task) => {
        const assignments = getTaskAssignments(task.taskId);
        let assigneeKey;

        if (!assignments || assignments.length === 0) {
          assigneeKey = "Unassigned";
        } else {
          // Use all assignees for grouping (create separate entries for each assignee)
          // This way tasks with multiple assignees appear in multiple columns
          assignments.forEach((assignment) => {
            const assigneeName = resolveAssigneeName(assignment.assignedUserId);
            if (!assigneeMap.has(assigneeName)) {
              assigneeMap.set(assigneeName, []);
            }
            assigneeMap.get(assigneeName).push(task);
          });
          return; // Skip the single assignee handling below since we already processed all assignees
        }

        if (!assigneeMap.has(assigneeKey)) {
          assigneeMap.set(assigneeKey, []);
        }
        assigneeMap.get(assigneeKey).push(task);
      });

      // Convert map to object and sort
      Array.from(assigneeMap.keys())
        .sort()
        .forEach((key) => {
          columns[key] = assigneeMap.get(key);
        });
    } else if (boardGroupBy === "stage") {
      // Group by stage - use the stage names from filteredStageTasks keys
      if (filteredStageTasks) {
        Object.entries(filteredStageTasks).forEach(([stageId, tasks]) => {
          if (tasks && tasks.length > 0) {
            const stageName = stageId === "no-stage" ? "No Stage" : stageId;
            if (!columns[stageName]) {
              columns[stageName] = [];
            }
            columns[stageName].push(...tasks);
          }
        });
      }
    }

    console.log("Board columns generated:", Object.keys(columns));
    return columns;
  };

  // Use either the passed getBoardColumns or our custom one
  const boardColumns = getBoardColumns || getColumnsWithResolvedNames();

  return (
    <div className="pbt-project-board-wrapper">
      {/* Header */}
      <div className="pbt-board-header">
        <div className="pbt-header-left">
          <h5 className="pbt-header-title">Board View</h5>

          <Form.Select
            size="sm"
            value={boardGroupBy}
            onChange={(e) => setBoardGroupBy(e.target.value)}
            className="pbt-group-selector"
          >
            <option value="status">Group by Status</option>
            <option value="priority">Group by Priority</option>
            <option value="assignee">Group by Assignee</option>
            <option value="stage">Group by Stage</option>
          </Form.Select>
        </div>
      </div>

      {/* Board Columns */}
      <div className="pbt-board-scroll-container">
        <div className="pbt-board-columns-wrapper">
          {Object.entries(boardColumns).map(([columnName, columnTasks]) => (
            <div key={columnName} className="pbt-board-column">
              <div className="pbt-column-header">
                <h6 className="pbt-column-title">{columnName}</h6>
                <span className="pbt-task-count-badge">
                  {columnTasks.length}
                </span>
              </div>

              <div className="pbt-column-tasks-container">
                {columnTasks.map((task) => (
                  <Card
                    key={task.taskId}
                    className="pbt-task-card"
                    style={{ cursor: "pointer" }}
                  >
                    <Card.Body className="pbt-task-card-body">
                      <div className="pbt-task-title">{task.taskName}</div>

                      <small className="pbt-task-description">
                        {task.taskDescription || "-"}
                      </small>

                      <div className="pbt-task-badges">
                        <Badge
                          className={`pbt-priority-badge ${getPriorityBadge(task.taskPriority)}`}
                        >
                          {task.taskPriority || "No Priority"}
                        </Badge>

                        <Badge
                          className={`pbt-status-badge ${getStatusBadge(task.taskStatus)}`}
                        >
                          {getTaskStatusName?.(task.taskStatus) || "No Status"}
                        </Badge>
                      </div>
                    </Card.Body>

                    {/* Hover Detail Panel */}
                    <div className="pbt-hover-detail-panel">
                      <div className="pbt-hover-header">
                        <h6 className="pbt-hover-title">{task.taskName}</h6>
                        <i
                          className="fas fa-pencil-alt pbt-edit-icon"
                          onClick={() => handleEditTask(task.taskId)}
                        ></i>
                      </div>

                      <hr className="pbt-hover-divider" />

                      <div className="pbt-hover-field">
                        <strong>Description:</strong>
                        <span>{task.taskDescription || "No description"}</span>
                      </div>

                      <div className="pbt-hover-field">
                        <strong>Priority:</strong>
                        <span>{task.taskPriority || "No Priority"}</span>
                      </div>

                      <div className="pbt-hover-field">
                        <strong>Status:</strong>
                        <span>
                          {getTaskStatusName?.(task.taskStatus) || "No Status"}
                        </span>
                      </div>

                      <div className="pbt-hover-field">
                        <strong>Start:</strong>
                        <span>
                          {task.taskStartDate
                            ? moment(task.taskStartDate).format("MMM DD, YYYY")
                            : "-"}
                        </span>
                      </div>

                      <div className="pbt-hover-field">
                        <strong>End:</strong>
                        <span>
                          {task.taskEndDate
                            ? moment(task.taskEndDate).format("MMM DD, YYYY")
                            : "-"}
                        </span>
                      </div>

                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => handleEditTask(task.taskId)}
                        className="pbt-hover-edit-btn"
                      >
                        Edit Task
                      </Button>
                    </div>
                  </Card>
                ))}

                {columnTasks.length === 0 && (
                  <div className="pbt-empty-column-message">No tasks</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectBoardTab;

// import React from "react";
// import { Form, Button, Card, Badge } from "react-bootstrap";
// import moment from "moment";

// const ProjectBoardTab = ({
//   boardGroupBy,
//   setBoardGroupBy,
//   getBoardColumns,
//   getPriorityBadge,
//   getStatusBadge,
//   handleEditTask,
// }) => {
//   return (
//     <div className="project-view-fade-in">
//       {/* Header */}
//       <div className="d-flex justify-content-between align-items-center mb-3">
//         <div className="d-flex align-items-center">
//           <h5 className="text-white me-3">Board View</h5>

//           <Form.Select
//             size="sm"
//             value={boardGroupBy}
//             onChange={(e) => setBoardGroupBy(e.target.value)}
//             style={{ width: "200px" }}
//           >
//             <option value="status">Group by Status</option>
//             <option value="priority">Group by Priority</option>
//             <option value="assignee">Group by Assignee</option>
//             <option value="stage">Group by Stage</option>
//           </Form.Select>
//         </div>
//       </div>

//       {/* Board Columns */}
//       <div className="project-view-board-container d-flex overflow-auto">
//         {Object.entries(getBoardColumns()).map(([columnName, columnTasks]) => (
//           <div key={columnName} className="project-view-board-column me-3">
//             <div className="project-view-board-column-header">
//               <h6>{columnName}</h6>
//               <span className="badge bg-secondary">{columnTasks.length}</span>
//             </div>

//             <div className="project-view-board-column-body">
//               {columnTasks.map((task) => (
//                 <Card
//                   key={task.taskId}
//                   className="project-view-board-task-card mb-2"
//                   style={{ cursor: "pointer" }}
//                 >
//                   <Card.Body>
//                     <div className="fw-bold">{task.taskName}</div>

//                     <small className="text-muted">
//                       {task.taskDescription || "-"}
//                     </small>

//                     <div className="mt-2 d-flex justify-content-between">
//                       <Badge className={getPriorityBadge(task.taskPriority)}>
//                         {task.taskPriority || "No Priority"}
//                       </Badge>

//                       <Badge className={getStatusBadge(task.taskStatus)}>
//                         {task.taskStatus || "No Status"}
//                       </Badge>
//                     </div>
//                   </Card.Body>

//                   {/* Hover Detail Panel */}
//                   <div className="project-view-board-hover-detail">
//                     <div className="hover-header d-flex justify-content-between align-items-center">
//                       <h6 className="mb-0">{task.taskName}</h6>

//                       <i
//                         className="fas fa-pencil-alt project-view-edit-icon"
//                         onClick={() => handleEditTask(task.taskId)}
//                       ></i>
//                     </div>

//                     <hr />

//                     <p>
//                       <strong>Description:</strong>{" "}
//                       {task.taskDescription || "No description"}
//                     </p>

//                     <p>
//                       <strong>Priority:</strong>{" "}
//                       {task.taskPriority || "No Priority"}
//                     </p>
//                     <p>
//                       <strong>Status:</strong> {task.taskStatus || "No Status"}
//                     </p>

//                     <p>
//                       <strong>Start:</strong>{" "}
//                       {task.taskStartDate
//                         ? moment(task.taskStartDate).format("MMM DD, YYYY")
//                         : "-"}
//                     </p>
//                     <p>
//                       <strong>End:</strong>{" "}
//                       {task.taskEndDate
//                         ? moment(task.taskEndDate).format("MMM DD, YYYY")
//                         : "-"}
//                     </p>

//                     <Button
//                       size="sm"
//                       variant="outline-primary"
//                       onClick={() => handleEditTask(task.taskId)}
//                       className="w-100 mt-2"
//                     >
//                       Edit Task
//                     </Button>
//                   </div>
//                 </Card>
//               ))}

//               {columnTasks.length === 0 && (
//                 <div className="text-muted text-center py-3">No tasks</div>
//               )}
//             </div>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// };

// export default ProjectBoardTab;
